import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { ADMIN_NAV } from "./_nav";
import { api, errFrom } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { AvailabilityGrid } from "../employee/Schedule";
import { formatWeekLabel } from "../../lib/schedule";
import { CheckCircle2, XCircle, Mail, Edit3, CalendarDays } from "lucide-react";

export default function AdminSchedules() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, pending_change_requests: 0, approved: 0 });
  const [decision, setDecision] = useState(null); // {record, type: "approve"|"reject"}
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    try {
      const [p, a, r, c] = await Promise.all([
        api.get("/admin/schedules", { params: { status: "pending" } }),
        api.get("/admin/schedules", { params: { status: "approved" } }),
        api.get("/admin/schedules", { params: { status: "rejected" } }),
        api.get("/admin/schedules/counts"),
      ]);
      setPending(p.data); setApproved(a.data); setRejected(r.data); setCounts(c.data);
    } catch (e) { toast.error(errFrom(e)); }
  }
  useEffect(() => { load(); }, []);

  async function submitDecision() {
    if (!decision) return;
    setLoading(true);
    try {
      await api.post(`/admin/schedules/${decision.record.id}/${decision.type}`, { admin_note: note || undefined });
      toast.success(decision.type === "approve" ? "Approved" : "Rejected");
      setDecision(null); setNote("");
      load();
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  return (
    <AppShell nav={ADMIN_NAV} title="Schedules" subtitle="Employee availability">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Pending" value={counts.pending} icon={<CalendarDays size={14} />} accent />
        <Stat label="Change requests" value={counts.pending_change_requests} icon={<Edit3 size={14} />} />
        <Stat label="Approved" value={counts.approved} icon={<CheckCircle2 size={14} />} />
        <Stat label="Rejected" value={rejected.length} icon={<XCircle size={14} />} />
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="bg-[#0A0A0A] border border-white/10 h-11">
          <TabsTrigger value="pending" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white px-6" data-testid="admin-schedules-tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white px-6" data-testid="admin-schedules-tab-approved">Approved ({approved.length})</TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white px-6" data-testid="admin-schedules-tab-rejected">Rejected ({rejected.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-6">
          <ScheduleList items={pending} onApprove={(r) => { setDecision({ record: r, type: "approve" }); setNote(""); }} onReject={(r) => { setDecision({ record: r, type: "reject" }); setNote(""); }} emptyText="No pending schedules. Nice — all caught up!" showActions />
        </TabsContent>
        <TabsContent value="approved" className="mt-6"><ScheduleList items={approved} emptyText="No approved schedules yet." /></TabsContent>
        <TabsContent value="rejected" className="mt-6"><ScheduleList items={rejected} emptyText="No rejected schedules." /></TabsContent>
      </Tabs>

      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent className="bg-[#141414] border-white/10 text-white" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{decision?.type === "approve" ? "Approve schedule" : "Reject schedule"}</DialogTitle>
            {decision && (
              <div className="text-sm text-zinc-400">{decision.record.user_name} · {formatWeekLabel(decision.record.week_start)} {decision.record.is_change_request && <span className="text-[#FF3B30]">· Change request</span>}</div>
            )}
          </DialogHeader>
          <div className="py-2 space-y-3">
            {decision && <AvailabilityGrid availability={decision.record.availability} />}
            {decision?.record?.note && (<div className="text-xs text-zinc-500 italic">Employee note: "{decision.record.note}"</div>)}
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to employee…" className="bg-[#0A0A0A] border-white/10" rows={2} data-testid="admin-note-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecision(null)} className="text-zinc-400">Cancel</Button>
            <Button onClick={submitDecision} disabled={loading}
              className={decision?.type === "approve" ? "bg-green-600 hover:bg-green-500" : "bg-red-600 hover:bg-red-500"}
              data-testid="confirm-decision-button">
              {decision?.type === "approve" ? <><CheckCircle2 size={14} className="mr-2" />Approve</> : <><XCircle size={14} className="mr-2" />Reject</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function ScheduleList({ items, onApprove, onReject, emptyText, showActions }) {
  if (!items.length) return <div className="brand-surface rounded-2xl p-12 text-center text-zinc-500">{emptyText}</div>;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {items.map((r) => (
        <div key={r.id} className="brand-surface rounded-2xl p-5" data-testid={`schedule-card-${r.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading font-bold text-lg">{r.user_name}</div>
              <div className="text-xs text-zinc-500 flex items-center gap-1"><Mail size={11} />{r.user_email}</div>
            </div>
            <div className="text-right">
              <div className="text-overline">Week of</div>
              <div className="text-sm font-semibold">{formatWeekLabel(r.week_start)}</div>
            </div>
          </div>

          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <StatusBadge status={r.status} />
            {r.is_change_request && <Badge className="bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30 border text-[10px] uppercase tracking-widest">Change request</Badge>}
          </div>

          <AvailabilityGrid availability={r.availability} />

          {r.note && <div className="mt-3 text-xs text-zinc-500 italic">"{r.note}"</div>}
          {r.admin_note && <div className="mt-2 text-xs"><span className="text-[#FF3B30] font-semibold">Admin:</span> <span className="text-zinc-300">{r.admin_note}</span></div>}
          {r.reviewed_at && <div className="mt-2 text-[10px] text-zinc-600">Reviewed {new Date(r.reviewed_at).toLocaleString()} by {r.reviewed_by_name}</div>}

          {showActions && (
            <div className="mt-4 flex gap-2">
              <Button size="sm" onClick={() => onApprove(r)} className="bg-green-600 hover:bg-green-500 flex-1 h-9" data-testid={`approve-schedule-${r.id}`}><CheckCircle2 size={14} className="mr-1" />Approve</Button>
              <Button size="sm" onClick={() => onReject(r)} variant="ghost" className="text-red-400 hover:bg-red-500/10 hover:text-red-300 flex-1 h-9" data-testid={`reject-schedule-${r.id}`}><XCircle size={14} className="mr-1" />Reject</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: ["bg-amber-500/15 text-amber-400 border-amber-500/30", "Pending"],
    approved: ["bg-green-500/15 text-green-400 border-green-500/30", "Approved"],
    rejected: ["bg-red-500/15 text-red-400 border-red-500/30", "Rejected"],
    superseded: ["bg-zinc-500/15 text-zinc-400 border-zinc-500/30", "Superseded"],
  };
  const [cls, label] = map[status] || ["bg-zinc-500/15 text-zinc-400 border-zinc-500/30", status];
  return <Badge className={`${cls} border text-[10px] uppercase tracking-widest font-semibold`}>{label}</Badge>;
}

function Stat({ label, value, icon, accent }) {
  return (
    <div className={`brand-surface rounded-xl p-4 ${accent ? "border-[#FF3B30]/30" : ""}`} data-testid={`admin-schedule-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between text-zinc-500">
        <span className="text-overline">{label}</span>
        <span className={accent ? "text-[#FF3B30]" : "text-zinc-600"}>{icon}</span>
      </div>
      <div className={`font-mono font-bold text-3xl mt-3 ${accent ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
    </div>
  );
}
